import type { OpenFeatureSupportedFlags } from '../../openfeature/types';
import type { FlagKey } from './types';

export const flags = {
  'pricing-social-proof': {
    enabled: [true, false],
  },
  'is-education-plans-routing': {
    enabled: [true, false],
  },
  'new-latest-results-widget': {
    enabled: [true, false],
  },
  'live-editor-csat': {
    enabled: [true, false],
  },
  'nux-editor-checklist': {
    enabled: [true, false],
  },
  'nux-business-training': {
    enabled: [true, false],
  },
  'nux-help-popover-in-editor': {
    enabled: [true, false],
  },
  'nux-mobile-intent-onboarding': {
    enabled: [true, false],
  },
  'idx-release-new-auth-flow-google': {
    enabled: [true, false],
  },
  'idx-release-new-auth-flow-facebook': {
    enabled: [true, false],
  },
  'idx-release-new-auth-flow-windows': {
    enabled: [true, false],
  },
  'ins-reset-results-v2': {
    enabled: [true, false],
  },
  'ins-call-new-endpoints': {
    enabled: ['internal', 'dev', 'disabled'],
  },
  'ins-results-summary-cards': {
    enabled: [true, false],
  },
  'nux-interactive-quick-actions': {
    enabled: [true, false],
  },
  'ai-menti-builder': {
    version: ['v1', 'v2', 'v3', 'conv-ui-v1', 'off'],
  },
  'mx-create-migrated-presentations': {
    enabled: [true, false],
  },
  'bees-actionable-feedback': {
    version: [
      'stars_only',
      'stars_and_structured_feedback',
      'stars_and_unstructured_feedback',
      'off',
    ],
  },
  'bees-participant-rating-on-results-page': {
    enabled: [true, false],
  },
  'bees-screenshot-in-preview': {
    enabled: [true, false],
  },
  'og-homepage-personalized-header': {
    enabled: [true, false],
  },
  'og-google-signup': {
    version: ['v1', 'v2'],
  },
  'mangos-find-and-join-workspace': {
    enabled: [true, false],
  },
  'mangos-discover-and-join-workspace-settings': {
    state: ['loading', 'enabled', 'disabled'],
  },
  'cx-canvas-slide-recommendations': {
    version: ['single-step', 'multi-step', 'off'],
  },
  'cx-hide-deprecated-slide-types': {
    enabled: [true, false],
  },
  'pay-discount-badges-cbc': {
    enabled: [true, false],
  },
  'pay-purchase-to-upgrade': {
    enabled: [true, false],
  },
  'biz-promote-enterprise-banner': {
    enabled: [true, false],
  },
  'pug-recommend-plan-onboarding': {
    enabled: [true, false],
  },
  'pug-onboarding-plans-page-aa': {
    enabled: [true, false],
  },
  'pug-value-oriented-pricing-page': {
    enabled: [true, false],
  },
  'sli-new-look-guess-the-number': {
    enabled: [true, false],
  },
  'sli-new-look-ranking': {
    enabled: [true, false],
  },
  'sli-new-look-word-cloud': {
    enabled: [true, false],
  },
} as const satisfies OpenFeatureSupportedFlags;

export const flagsMetadata: Record<
  FlagKey,
  { description: string; displayName: string }
> = {
  'pricing-social-proof.enabled': {
    displayName: 'PUG011 - Social proof on plans page',
    description: 'Show client logos on the pricing page',
  },
  'is-education-plans-routing.enabled': {
    displayName: 'PAY02 - Education plans routing',
    description:
      'Route to education plans when knowing that the user is an educational user',
  },
  'new-latest-results-widget.enabled': {
    displayName: 'PUG012 - Increase adoption of Results from Home',
    description: 'Show a widget with extra results information',
  },
  'live-editor-csat.enabled': {
    displayName: 'LIVE - Show CSAT Form',
    description: 'Show the CSAT form to the user in the editor.',
  },
  'nux-editor-checklist.enabled': {
    displayName: 'NUX - Editor Checklist',
    description: 'Show a checklist in the editor',
  },
  'nux-business-training.enabled': {
    displayName: 'NUX - Personalized Business Training Quick Actions',
    description: 'Show Business Training Quick Actions in the NUX',
  },
  'nux-mobile-intent-onboarding.enabled': {
    displayName: 'NUX - Mobile Intent Onboarding',
    description: 'Enable mobile intent onboarding for new users',
  },
  'idx-release-new-auth-flow-google.enabled': {
    displayName: 'Google - New Auth Flow',
    description: 'Use the new Google auth flow for sign up and login',
  },
  'idx-release-new-auth-flow-facebook.enabled': {
    displayName: 'Facebook - New Auth Flow',
    description: 'Use the new Facebook auth flow for login',
  },
  'idx-release-new-auth-flow-windows.enabled': {
    displayName: 'Windows - New Auth Flow',
    description: 'Use the new Windows auth flow for login',
  },
  'ins-reset-results-v2.enabled': {
    displayName: 'INS - New way of resetting results',
    description: 'In dashboard, results page and editor',
  },
  'ins-call-new-endpoints.enabled': {
    displayName: 'INS - Call new endpoints',
    description: 'Call the new endpoints for results page',
  },
  'ins-results-summary-cards.enabled': {
    displayName: 'INS - Summary cards',
    description: 'Show summary cards in the Results page',
  },
  'nux-interactive-quick-actions.enabled': {
    displayName: 'NUX - Interactive Quick Actions',
    description: 'Show an additional interactive step in the onboarding',
  },
  'nux-help-popover-in-editor.enabled': {
    displayName: 'NUX - Help popover in the editor',
    description:
      'Automatically show the help modal for first time users in the editor',
  },
  'ai-menti-builder.version': {
    displayName: 'AI001 - AI Menti Builder',
    description: 'Show the AI Menti Builder button in the dashboard',
  },
  'mx-create-migrated-presentations.enabled': {
    displayName: 'MX - Create migrated presentations',
    description: 'Create migrated presentations',
  },
  'bees-actionable-feedback.version': {
    displayName: '🐝💬 BEES - Participant experiment with different feedback',
    description: 'Experiment with feedback on the end screen',
  },
  'bees-participant-rating-on-results-page.enabled': {
    displayName: 'BEES - Participant rating on results page',
    description: 'Show participants rating on results page',
  },
  'bees-screenshot-in-preview.enabled': {
    displayName: 'BEES - Screenshot in Dashboard Preview',
    description: 'Experiment with screenshots in previews in the dashboard',
  },
  'og-homepage-personalized-header.enabled': {
    displayName: 'OG - Homepage Personalized Header',
    description: 'Show a personalized header on the homepage for executives',
  },
  'og-google-signup.version': {
    displayName: 'OG - Downprioritize Google Signup',
    description: 'Downprioritize Google Signup experiment in the signup flow',
  },
  'mangos-find-and-join-workspace.enabled': {
    displayName: 'MANGOS - Find and join workspace',
    description:
      'Allow new users to find and join a workspace in the onboarding process',
  },
  'mangos-discover-and-join-workspace-settings.state': {
    displayName: 'MANGOS - Discover and join workspace settings',
    description:
      'New UI for the settings page to discover and join a workspace',
  },
  'cx-canvas-slide-recommendations.version': {
    displayName: 'CX - Slide recommendations in the canvas',
    description:
      'Enables slide recommendations in a new presentation’s empty state',
  },

  'cx-hide-deprecated-slide-types.enabled': {
    displayName: 'CX - Hide deprecated slide types',
    description: 'Hide deprecated slide types in the add slide popover',
  },
  'pay-discount-badges-cbc.enabled': {
    displayName: 'PAY - Discount Badges in CBC',
    description: 'Show discount badges in the custom built checkout',
  },
  'pay-purchase-to-upgrade.enabled': {
    displayName: 'PAY - Purchase to upgrade',
    description:
      'A flow for purchasing a new license and upgrade to member in one click',
  },
  'biz-promote-enterprise-banner.enabled': {
    displayName: 'BIZ - Promote Enterprise Banner',
    description:
      'Show the enterprise banner in the dashboard for organization leaders',
  },
  'pug-recommend-plan-onboarding.enabled': {
    displayName: 'PUG - Recommend plan in onboarding',
    description: 'Show a recommendation for the plan in the onboarding process',
  },
  'pug-onboarding-plans-page-aa.enabled': {
    displayName: 'PUG - Onboarding plans page A/B test',
    description: 'Show a new onboarding plans page',
  },
  'pug-value-oriented-pricing-page.enabled': {
    displayName: 'PUG - Value-oriented pricing page',
    description: 'Show a value-oriented pricing page',
  },
  'sli-new-look-guess-the-number.enabled': {
    displayName: 'Slides - New look for Guess the Number',
    description: 'New look for the Guess the Number visualization',
  },
  'sli-new-look-ranking.enabled': {
    displayName: 'Slides - New look for Ranking',
    description: 'New look for the Ranking visualization',
  },
  'sli-new-look-word-cloud.enabled': {
    displayName: 'Slides - New look for WordCloud',
    description: 'New look for the WordCloud visualization',
  },
};
